import * as React from 'react';
import { connect } from 'react-redux';
import { withTheme } from 'styled-components';
import { localize } from 'core/localization';
import { BUTTON } from 'constants/components';
import { Button, Tooltip } from 'components/common';
import { getColors } from 'store/settings/selectors';
import { isAttemptSubmitted } from 'store/course/selectors';
import {
  getAnsweredAffectProgressQuestionsCount,
  getAffectProgressQuestionsCount
} from 'store/questions/selectors';

import { RootAppState } from 'store/types';
import { KEYS } from 'constants/common';
import {
  SubmitMessageWrapper,
  PopoverContentWrapper,
  PopoverContent,
  ClosePopoverIcon,
  PopoverContentHeader,
  PopoverContentMessage,
  PopoverButton
} from './SubmitOnceButton.styled';

type SubmitOnceButtonProps = {
  onClick(): void;
  isActive: boolean;
  open: boolean;
  theme?: any;
  colors?: { [key: string]: any };
  showSubmitButton?: boolean;
  remainingQuestions?: number;
  affectProgressQuestionsCount?: number;
  tabIndex: number;
};

type SubmitOnceButtonState = {
  open: boolean;
};

export class SubmitOnceButton extends React.PureComponent<
  SubmitOnceButtonProps,
  SubmitOnceButtonState
> {
  popoverRef: any;

  popoverContentRef: any;

  constructor(props: SubmitOnceButtonProps) {
    super(props);
    this.state = { open: false };
    this.popoverRef = React.createRef();
    this.popoverContentRef = React.createRef();
  }

  static defaultProps = {
    open: false,
    isActive: false
  };

  setTooltipAction = (option: any) => {
    this.setState({
      open: option
    });
  };

  closePopup = () => {
    this.setTooltipAction(false);
    setTimeout(() => {
      this.popoverRef.current.children[0].focus();
    }, 0);
  };

  componentDidUpdate() {
    setTimeout(() => {
      if (
        this.state.open &&
        this.popoverContentRef.current &&
        this.popoverContentRef.current.children.length
      ) {
        this.popoverContentRef.current.children[0].focus();
      }
    }, 0);
  }

  render() {
    const { open } = this.state;
    const { isActive, remainingQuestions, showSubmitButton, tabIndex } = this.props;
    return (
      <React.Fragment>
        {isActive &&
          showSubmitButton &&
          (remainingQuestions ? (
            <Tooltip
              style={{ display: 'block' }}
              trigger={'click'}
              open={open}
              onRequestClose={this.closePopup}
              position={'top'}
              tooltipTheme={'light'}
              arrow={true}
              interactive
              html={this.renderPopoverContent()}
            >
              <div ref={this.popoverRef}>
                <Button
                  tabIndex={tabIndex}
                  onClick={this.viewAction}
                  layout={BUTTON.LAYOUT.SECONDARY}
                  data-test="submit-all-answers-remaining"
                >
                  {this.submitAllAnswersText()}
                </Button>
              </div>
            </Tooltip>
          ) : (
            <Button
              tabIndex={tabIndex}
              onClick={this.submitOnce}
              data-test="submit-all-answers-completed"
            >
              {this.submitAllAnswersText()}
            </Button>
          ))}
      </React.Fragment>
    );
  }

  submitAllAnswersText = () => localize('[submit all answers]');

  handleKeyDown = (evt: any) => {
    if ([KEYS.ENTER.toLowerCase(), KEYS.ESCAPE.toLowerCase()].includes(evt.key.toLowerCase())) {
      this.closePopup();
    }
  };

  viewAction = () => {
    const { remainingQuestions } = this.props;
    if (!remainingQuestions) {
      this.submitOnce();
    } else {
      this.setTooltipAction(true);
    }
  };

  toggle = (evt: any) => {
    if (!evt.key) {
      const { open } = this.state;
      this.setTooltipAction(!open);
    }
  };

  renderPopoverContent = () => {
    const templateTheme = this.props.theme;
    const { remainingQuestions, tabIndex } = this.props;
    return (
      <SubmitMessageWrapper>
        <PopoverContentWrapper
          id="progress-popover"
          role="dialog"
          ariaModal={true}
          tabIndex={-1}
          onKeyDown={event => this.handleKeyDown(event)}
          theme={templateTheme}
        >
          <PopoverContent ref={this.popoverContentRef}>
            <ClosePopoverIcon
              role="button"
              ariaLabel={localize('[close popover]')}
              tabIndex={tabIndex}
              onClick={this.toggle}
              size={14}
              name="close-popup"
              theme={templateTheme}
            />
            <PopoverContentHeader theme={templateTheme} appearance="h3">
              {localize('[you did not answer all questions]')}
            </PopoverContentHeader>
            <PopoverContentMessage theme={templateTheme} data-test="remaining-questions">
              ({localize('[x question remaining]', { remainingQuestions })})
            </PopoverContentMessage>

            <PopoverButton
              tabIndex={tabIndex}
              onClick={this.closePopup}
              theme={templateTheme}
              data-test="continue-course-button"
            >
              {localize('[continue course]')}
            </PopoverButton>
            <PopoverButton
              tabIndex={tabIndex}
              onClick={this.submitOnce}
              theme={templateTheme}
              layout={BUTTON.LAYOUT.SECONDARY}
              data-test="submit-all-answers-pop-up"
            >
              {localize('[submit all answers]')}
            </PopoverButton>
          </PopoverContent>
        </PopoverContentWrapper>
      </SubmitMessageWrapper>
    );
  };

  submitOnce = () => {
    this.props.onClick();
  };
}

function mapStateToProps(state: RootAppState) {
  return {
    colors: getColors(state),
    showSubmitButton: !isAttemptSubmitted(state),
    remainingQuestions:
      getAffectProgressQuestionsCount(state) - getAnsweredAffectProgressQuestionsCount(state)
  };
}

export default withTheme(connect(mapStateToProps)(SubmitOnceButton));
